import { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import { Users, Settings as SettingsIcon, Shield, Trash2, Edit, Plus, LayoutDashboard, Map, UserCircle, RotateCcw, Upload, Download, Database, FileJson, FileSpreadsheet, FileText, Mail } from 'lucide-react';
import api from '../services/api';

const Settings = () => {
  const [activeTab, setActiveTab] = useState('users');
  const [editingUser, setEditingUser] = useState(null);
  const [showUserModal, setShowUserModal] = useState(false);
  const queryClient = useQueryClient();

  const tabs = [
    { id: 'users', label: 'User Management', icon: Users },
    { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard },
    { id: 'mapview', label: 'Map View', icon: Map },
    { id: 'customers', label: 'Customers', icon: UserCircle },
    { id: 'import', label: 'Data Import', icon: Upload },
    { id: 'backup', label: 'Data Backup', icon: Database },
    { id: 'system', label: 'System', icon: SettingsIcon },
    { id: 'security', label: 'Security', icon: Shield }
  ];

  return (
    <div className="space-y-6">
      <h1 className="text-3xl font-bold">Settings</h1>

      {/* Tabs */}
      <div className="flex space-x-4 border-b border-gray-200 overflow-x-auto">
        {tabs.map(tab => {
          const Icon = tab.icon;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-3 border-b-2 font-medium transition-colors whitespace-nowrap ${
                activeTab === tab.id
                  ? 'border-primary-600 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <Icon className="w-5 h-5" />
              {tab.label}
            </button>
          );
        })}
      </div>

      {/* Tab Content */}
      <div>
        {activeTab === 'users' && (
          <UserManagement
            editingUser={editingUser}
            setEditingUser={setEditingUser}
            showUserModal={showUserModal}
            setShowUserModal={setShowUserModal}
          />
        )}
        {activeTab === 'dashboard' && <DashboardSettings />}
        {activeTab === 'mapview' && <MapViewSettings />}
        {activeTab === 'customers' && <CustomerSettings />}
        {activeTab === 'import' && <DataImport />}
        {activeTab === 'backup' && <DataBackup />}
        {activeTab === 'system' && <SystemSettings />}
        {activeTab === 'security' && <SecuritySettings />}
      </div>
    </div>
  );
};

// User Management Tab (unchanged)
const UserManagement = ({ editingUser, setEditingUser, showUserModal, setShowUserModal }) => {
  const queryClient = useQueryClient();

  const { data: users, isLoading } = useQuery('users', async () => {
    const res = await api.get('/users');
    return res.data.data;
  }, {
    // Disable automatic refetching to prevent modal remount issues
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchInterval: false
  });

  const deleteMutation = useMutation(
    (userId) => api.delete(`/users/${userId}`),
    {
      onSuccess: () => {
        toast.success('User deleted successfully');
        queryClient.invalidateQueries('users');
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Failed to delete user');
      }
    }
  );

  const handleDelete = (user) => {
    if (window.confirm(`Are you sure you want to delete user "${user.username}"?`)) {
      deleteMutation.mutate(user.id);
    }
  };

  // Quick edit using prompt - bypasses all React form issues
  const handleQuickEditEmail = async (user) => {
    const newEmail = window.prompt('Enter new email:', user.email);
    if (newEmail && newEmail !== user.email) {
      try {
        await api.put(`/users/${user.id}`, { email: newEmail });
        toast.success('Email updated successfully');
        queryClient.invalidateQueries('users');
      } catch (error) {
        toast.error(error.response?.data?.message || 'Failed to update email');
      }
    }
  };

  const handleEdit = (user) => {
    setEditingUser(user);
    setShowUserModal(true);
  };

  const handleAddNew = () => {
    setEditingUser(null);
    setShowUserModal(true);
  };

  if (isLoading) return <div>Loading users...</div>;

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-semibold">Users</h2>
        <button onClick={handleAddNew} className="btn btn-primary flex items-center gap-2">
          <Plus className="w-4 h-4" />
          Add User
        </button>
      </div>

      <div className="card overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Username</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Role</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {users?.map(user => (
              <tr key={user.id}>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="font-medium text-gray-900">{user.username}</div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-gray-500">{user.email}</div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className={`px-2 py-1 text-xs font-semibold rounded-full ${
                    user.role === 'admin' ? 'bg-red-100 text-red-800' :
                    user.role === 'manager' ? 'bg-blue-100 text-blue-800' :
                    user.role === 'technician' ? 'bg-green-100 text-green-800' :
                    'bg-gray-100 text-gray-800'
                  }`}>
                    {user.role}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className={`px-2 py-1 text-xs font-semibold rounded-full ${
                    user.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                  }`}>
                    {user.isActive ? 'Active' : 'Inactive'}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm">
                  <div className="flex gap-1">
                    <button type="button" onClick={() => handleQuickEditEmail(user)} className="p-2 text-green-600 hover:text-green-800 hover:bg-green-50 rounded" title="Quick Edit Email">
                      <Mail className="w-5 h-5" />
                    </button>
                    <button type="button" onClick={() => handleEdit(user)} className="p-2 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded" title="Edit User">
                      <Edit className="w-5 h-5" />
                    </button>
                    <button type="button" onClick={() => handleDelete(user)} className="p-2 text-red-600 hover:text-red-800 hover:bg-red-50 rounded" title="Delete User">
                      <Trash2 className="w-5 h-5" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showUserModal && (
        <UserModal user={editingUser} onClose={() => { setShowUserModal(false); setEditingUser(null); }} />
      )}
    </div>
  );
};

// User Modal - Using native FormData API
const UserModal = ({ user, onClose }) => {
  const queryClient = useQueryClient();
  const [showResetPassword, setShowResetPassword] = useState(false);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const formRef = useRef(null);

  // Password reset form
  const { register: registerPassword, handleSubmit: handleSubmitPassword, formState: { errors: passwordErrors }, watch, reset: resetPasswordForm } = useForm();

  const resetPasswordMutation = useMutation(
    (data) => api.post(`/auth/reset-password/${user.id}`, data),
    {
      onSuccess: () => {
        toast.success('Password reset successfully');
        resetPasswordForm();
        setShowResetPassword(false);
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Failed to reset password');
      }
    }
  );

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Use native FormData API to read values directly from form
    const nativeFormData = new FormData(formRef.current);
    const formValues = {
      username: nativeFormData.get('username') || '',
      email: nativeFormData.get('email') || '',
      password: nativeFormData.get('password') || '',
      role: nativeFormData.get('role') || 'viewer',
      isActive: nativeFormData.get('isActive') === 'on'
    };

    // Validate
    const newErrors = {};
    if (!user && !formValues.username) newErrors.username = 'Username is required';
    if (!formValues.email) newErrors.email = 'Email is required';
    if (!user && !formValues.password) newErrors.password = 'Password is required';

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    // Submit - remove empty password
    const submitData = { ...formValues };
    if (!submitData.password) {
      delete submitData.password;
    }

    setIsSubmitting(true);
    try {
      if (user) {
        await api.put(`/users/${user.id}`, submitData);
      } else {
        await api.post('/users', submitData);
      }
      toast.success(`User ${user ? 'updated' : 'created'} successfully`);
      queryClient.invalidateQueries('users');
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Operation failed');
    } finally {
      setIsSubmitting(false);
    }
  };

  const onResetPassword = (data) => {
    resetPasswordMutation.mutate({ newPassword: data.newPassword });
  };

  // Use portal to render modal outside React component tree to prevent remounting
  return createPortal(
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg p-6 max-w-md w-full max-h-[90vh] overflow-y-auto">
        <h2 className="text-2xl font-bold mb-4">{user ? 'Edit User' : 'Add New User'}</h2>

        <form ref={formRef} onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="label">Username *</label>
            <input
              name="username"
              defaultValue={user?.username || ''}
              className="input"
              disabled={!!user}
            />
            {errors.username && <span className="text-red-500 text-sm">{errors.username}</span>}
          </div>

          <div>
            <label className="label">Email *</label>
            <input
              type="email"
              name="email"
              defaultValue={user?.email || ''}
              className="input"
              autoComplete="off"
            />
            {errors.email && <span className="text-red-500 text-sm">{errors.email}</span>}
          </div>

          {!user && (
            <div>
              <label className="label">Password *</label>
              <input
                type="password"
                name="password"
                defaultValue=""
                className="input"
              />
              {errors.password && <span className="text-red-500 text-sm">{errors.password}</span>}
            </div>
          )}

          <div>
            <label className="label">Role *</label>
            <select
              name="role"
              defaultValue={user?.role || 'viewer'}
              className="input"
            >
              <option value="admin">Admin</option>
              <option value="manager">Manager</option>
              <option value="technician">Technician</option>
              <option value="viewer">Viewer</option>
            </select>
          </div>

          <div className="flex items-center">
            <input
              type="checkbox"
              name="isActive"
              defaultChecked={user?.isActive ?? true}
              className="mr-2"
            />
            <label className="text-sm">Active</label>
          </div>

          <div className="flex gap-4 pt-4">
            <button type="submit" className="btn btn-primary" disabled={isSubmitting}>
              {isSubmitting ? 'Saving...' : 'Save'}
            </button>
            <button type="button" onClick={onClose} className="btn btn-secondary">
              Cancel
            </button>
          </div>
        </form>

        {/* Reset Password Section - Only for existing users */}
        {user && (
          <div className="mt-6 pt-6 border-t border-gray-200">
            <button
              type="button"
              onClick={() => setShowResetPassword(!showResetPassword)}
              className="btn btn-secondary w-full"
            >
              {showResetPassword ? 'Hide Password Reset' : 'Reset User Password'}
            </button>

            {showResetPassword && (
              <form onSubmit={handleSubmitPassword(onResetPassword)} className="mt-4 space-y-4">
                <div>
                  <label className="label">New Password</label>
                  <input
                    type="password"
                    {...registerPassword('newPassword', {
                      required: 'Password is required',
                      minLength: { value: 12, message: 'Password must be at least 12 characters' }
                    })}
                    className="input"
                  />
                  {passwordErrors.newPassword && <span className="text-red-500 text-sm">{passwordErrors.newPassword.message}</span>}
                </div>

                <div>
                  <label className="label">Confirm New Password</label>
                  <input
                    type="password"
                    {...registerPassword('confirmPassword', {
                      required: 'Please confirm password',
                      validate: (value) => value === watch('newPassword') || 'Passwords do not match'
                    })}
                    className="input"
                  />
                  {passwordErrors.confirmPassword && <span className="text-red-500 text-sm">{passwordErrors.confirmPassword.message}</span>}
                </div>

                <button type="submit" className="btn btn-primary w-full" disabled={resetPasswordMutation.isLoading}>
                  {resetPasswordMutation.isLoading ? 'Resetting...' : 'Reset Password'}
                </button>
              </form>
            )}
          </div>
        )}
      </div>
    </div>,
    document.body
  );
};

// Dashboard Settings Tab
const DashboardSettings = () => {
  const queryClient = useQueryClient();

  const { data: settings, isLoading } = useQuery('dashboard-settings', async () => {
    const res = await api.get('/settings/dashboard');
    return res.data.data;
  });

  const updateMutation = useMutation(
    (data) => api.put('/settings', { category: 'dashboard', settings: data }),
    {
      onSuccess: () => {
        toast.success('Dashboard settings updated');
        queryClient.invalidateQueries('dashboard-settings');
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Failed to update settings');
      }
    }
  );

  const resetMutation = useMutation(
    () => api.post('/settings/dashboard/reset'),
    {
      onSuccess: () => {
        toast.success('Dashboard settings reset to default');
        queryClient.invalidateQueries('dashboard-settings');
      }
    }
  );

  const { register, handleSubmit } = useForm({
    values: settings || {}
  });

  const onSubmit = (data) => {
    updateMutation.mutate({
      ...data,
      refreshInterval: parseInt(data.refreshInterval)
    });
  };

  if (isLoading) return <div>Loading...</div>;

  return (
    <div className="card space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-semibold">Dashboard Configuration</h2>
        <button onClick={() => resetMutation.mutate()} className="btn btn-secondary flex items-center gap-2">
          <RotateCcw className="w-4 h-4" />
          Reset to Default
        </button>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        <div>
          <label className="label">Refresh Interval (milliseconds)</label>
          <input type="number" {...register('refreshInterval')} className="input" />
          <p className="text-sm text-gray-500 mt-1">How often to refresh dashboard data (30000 = 30 seconds)</p>
        </div>

        <div>
          <label className="label">Default Date Range</label>
          <select {...register('defaultDateRange')} className="input">
            <option value="1d">Last 24 hours</option>
            <option value="7d">Last 7 days</option>
            <option value="30d">Last 30 days</option>
            <option value="90d">Last 90 days</option>
          </select>
        </div>

        <div className="space-y-2">
          <label className="label">Visible Widgets</label>
          <div className="flex items-center">
            <input type="checkbox" {...register('showStatistics')} className="mr-2" />
            <label className="text-sm">Show Statistics Cards</label>
          </div>
          <div className="flex items-center">
            <input type="checkbox" {...register('showRecentCustomers')} className="mr-2" />
            <label className="text-sm">Show Recent Customers</label>
          </div>
          <div className="flex items-center">
            <input type="checkbox" {...register('showStatusChart')} className="mr-2" />
            <label className="text-sm">Show Status Chart</label>
          </div>
        </div>

        <div className="pt-4">
          <button type="submit" className="btn btn-primary" disabled={updateMutation.isLoading}>
            {updateMutation.isLoading ? 'Saving...' : 'Save Dashboard Settings'}
          </button>
        </div>
      </form>
    </div>
  );
};

// Map View Settings Tab
const MapViewSettings = () => {
  const queryClient = useQueryClient();

  const { data: settings, isLoading } = useQuery('mapview-settings', async () => {
    const res = await api.get('/settings/mapView');
    return res.data.data;
  });

  const updateMutation = useMutation(
    (data) => api.put('/settings', { category: 'mapView', settings: data }),
    {
      onSuccess: () => {
        toast.success('Map view settings updated');
        queryClient.invalidateQueries('mapview-settings');
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Failed to update settings');
      }
    }
  );

  const resetMutation = useMutation(
    () => api.post('/settings/mapView/reset'),
    {
      onSuccess: () => {
        toast.success('Map view settings reset to default');
        queryClient.invalidateQueries('mapview-settings');
      }
    }
  );

  const { register, handleSubmit } = useForm({
    values: settings || {}
  });

  const onSubmit = (data) => {
    updateMutation.mutate({
      ...data,
      defaultCenter: {
        lat: parseFloat(data.defaultCenter?.lat || settings.defaultCenter.lat),
        lng: parseFloat(data.defaultCenter?.lng || settings.defaultCenter.lng)
      },
      defaultZoom: parseInt(data.defaultZoom)
    });
  };

  if (isLoading) return <div>Loading...</div>;

  return (
    <div className="card space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-semibold">Map View Configuration</h2>
        <button onClick={() => resetMutation.mutate()} className="btn btn-secondary flex items-center gap-2">
          <RotateCcw className="w-4 h-4" />
          Reset to Default
        </button>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="label">Default Center Latitude</label>
            <input type="number" step="0.0001" {...register('defaultCenter.lat')} className="input" />
          </div>
          <div>
            <label className="label">Default Center Longitude</label>
            <input type="number" step="0.0001" {...register('defaultCenter.lng')} className="input" />
          </div>
        </div>

        <div>
          <label className="label">Default Zoom Level</label>
          <input type="number" min="1" max="20" {...register('defaultZoom')} className="input" />
          <p className="text-sm text-gray-500 mt-1">Zoom level from 1 (world) to 20 (street level)</p>
        </div>

        <div className="flex items-center">
          <input type="checkbox" {...register('enableClustering')} className="mr-2" />
          <label className="text-sm">Enable Marker Clustering</label>
        </div>

        <div>
          <label className="label">Marker Colors by Status</label>
          <div className="space-y-2">
            {Object.entries(settings?.markerColors || {}).map(([status, color]) => (
              <div key={status} className="flex items-center gap-4">
                <div className="w-32 text-sm">{status}</div>
                <input type="color" {...register(`markerColors.${status}`)} className="h-10 w-20" />
              </div>
            ))}
          </div>
        </div>

        <div className="pt-4">
          <button type="submit" className="btn btn-primary" disabled={updateMutation.isLoading}>
            {updateMutation.isLoading ? 'Saving...' : 'Save Map Settings'}
          </button>
        </div>
      </form>
    </div>
  );
};

// Customer Settings Tab
const CustomerSettings = () => {
  const queryClient = useQueryClient();
  const [statusList, setStatusList] = useState([]);
  const [areaList, setAreaList] = useState([]);

  const { data: settings, isLoading } = useQuery('customer-settings', async () => {
    const res = await api.get('/settings/customers');
    return res.data.data;
  });

  // Sync state with fetched settings
  useEffect(() => {
    if (settings) {
      setStatusList(settings.statusOptions || []);
      setAreaList(settings.areaOptions || []);
    }
  }, [settings]);

  // Track if initial data has been loaded
  const [isInitialized, setIsInitialized] = useState(false);
  useEffect(() => {
    if (settings && !isInitialized) {
      setIsInitialized(true);
    }
  }, [settings, isInitialized]);

  const updateMutation = useMutation(
    (data) => api.put('/settings', { category: 'customers', settings: data }),
    {
      onSuccess: () => {
        toast.success('Customer settings updated');
        queryClient.invalidateQueries('customer-settings');
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Failed to update settings');
      }
    }
  );

  const resetMutation = useMutation(
    () => api.post('/settings/customers/reset'),
    {
      onSuccess: () => {
        toast.success('Customer settings reset to default');
        queryClient.invalidateQueries('customer-settings');
      }
    }
  );

  const { register, handleSubmit, reset } = useForm({
    values: settings || {}
  });

  // Reset form when settings change to ensure proper synchronization
  useEffect(() => {
    if (settings) {
      reset(settings);
    }
  }, [settings, reset]);

  const onSubmit = (data) => {
    updateMutation.mutate({
      ...data,
      itemsPerPage: parseInt(data.itemsPerPage),
      statusOptions: statusList,
      areaOptions: areaList
    });
  };

  if (isLoading || !isInitialized) return <div>Loading...</div>;

  return (
    <div className="card space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-semibold">Customer Configuration</h2>
        <button onClick={() => resetMutation.mutate()} className="btn btn-secondary flex items-center gap-2">
          <RotateCcw className="w-4 h-4" />
          Reset to Default
        </button>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        <div>
          <label className="label">Items Per Page</label>
          <select {...register('itemsPerPage')} className="input">
            <option value="10">10</option>
            <option value="20">20</option>
            <option value="50">50</option>
            <option value="100">100</option>
          </select>
        </div>

        <div>
          <label className="label">Default Status for New Customers</label>
          <select {...register('defaultStatus')} className="input">
            {statusList.map(status => (
              <option key={status} value={status}>{status}</option>
            ))}
          </select>
        </div>

        <div>
          <label className="label">Status Options</label>
          <div className="space-y-2">
            {statusList.map((status, idx) => (
              <div key={idx} className="flex items-center gap-2">
                <input
                  type="text"
                  value={status}
                  onChange={(e) => {
                    const newList = [...statusList];
                    newList[idx] = e.target.value;
                    setStatusList(newList);
                  }}
                  className="input flex-1"
                />
                <button
                  type="button"
                  onClick={() => setStatusList(statusList.filter((_, i) => i !== idx))}
                  className="text-red-600 hover:text-red-800"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
            <button
              type="button"
              onClick={() => setStatusList([...statusList, 'New Status'])}
              className="btn btn-secondary flex items-center gap-2"
            >
              <Plus className="w-4 h-4" />
              Add Status
            </button>
          </div>
        </div>

        <div>
          <label className="label">Area Options</label>
          <div className="space-y-2">
            {areaList.map((area, idx) => (
              <div key={idx} className="flex items-center gap-2">
                <input
                  type="text"
                  value={area}
                  onChange={(e) => {
                    const newList = [...areaList];
                    newList[idx] = e.target.value;
                    setAreaList(newList);
                  }}
                  className="input flex-1"
                />
                <button
                  type="button"
                  onClick={() => setAreaList(areaList.filter((_, i) => i !== idx))}
                  className="text-red-600 hover:text-red-800"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
            <button
              type="button"
              onClick={() => setAreaList([...areaList, 'New Area'])}
              className="btn btn-secondary flex items-center gap-2"
            >
              <Plus className="w-4 h-4" />
              Add Area
            </button>
          </div>
        </div>

        <div className="pt-4">
          <button type="submit" className="btn btn-primary" disabled={updateMutation.isLoading}>
            {updateMutation.isLoading ? 'Saving...' : 'Save Customer Settings'}
          </button>
        </div>
      </form>
    </div>
  );
};

// System Settings Tab
const SystemSettings = () => {
  const queryClient = useQueryClient();

  const { data: settings, isLoading } = useQuery('system-settings', async () => {
    const res = await api.get('/settings/system');
    return res.data.data;
  });

  const { data: notifications } = useQuery('notification-settings', async () => {
    const res = await api.get('/settings/notifications');
    return res.data.data;
  });

  const updateSystemMutation = useMutation(
    (data) => api.put('/settings', { category: 'system', settings: data }),
    {
      onSuccess: () => {
        toast.success('System settings updated');
        queryClient.invalidateQueries('system-settings');
      }
    }
  );

  const updateNotificationsMutation = useMutation(
    (data) => api.put('/settings', { category: 'notifications', settings: data }),
    {
      onSuccess: () => {
        toast.success('Notification settings updated');
        queryClient.invalidateQueries('notification-settings');
      }
    }
  );

  const { register: registerSystem, handleSubmit: handleSubmitSystem } = useForm({
    values: settings || {}
  });

  const { register: registerNotifications, handleSubmit: handleSubmitNotifications } = useForm({
    values: notifications || {}
  });

  if (isLoading) return <div>Loading...</div>;

  return (
    <div className="space-y-6">
      <div className="card space-y-6">
        <h2 className="text-xl font-semibold">System Configuration</h2>

        <form onSubmit={handleSubmitSystem(data => updateSystemMutation.mutate(data))} className="space-y-4">
          <div>
            <label className="label">Application Name</label>
            <input type="text" {...registerSystem('appName')} className="input" />
          </div>

          <div>
            <label className="label">Date Format</label>
            <select {...registerSystem('dateFormat')} className="input">
              <option value="DD/MM/YYYY">DD/MM/YYYY</option>
              <option value="MM/DD/YYYY">MM/DD/YYYY</option>
              <option value="YYYY-MM-DD">YYYY-MM-DD</option>
            </select>
          </div>

          <div>
            <label className="label">Time Format</label>
            <select {...registerSystem('timeFormat')} className="input">
              <option value="24h">24-hour</option>
              <option value="12h">12-hour (AM/PM)</option>
            </select>
          </div>

          <div>
            <label className="label">Timezone</label>
            <select {...registerSystem('timezone')} className="input">
              <option value="Europe/Athens">Europe/Athens (Cyprus)</option>
              <option value="Europe/London">Europe/London (GMT)</option>
              <option value="America/New_York">America/New_York (EST)</option>
              <option value="Asia/Dubai">Asia/Dubai (GST)</option>
            </select>
          </div>

          <div className="pt-4">
            <button type="submit" className="btn btn-primary" disabled={updateSystemMutation.isLoading}>
              {updateSystemMutation.isLoading ? 'Saving...' : 'Save System Settings'}
            </button>
          </div>
        </form>
      </div>

      <div className="card space-y-6">
        <h2 className="text-xl font-semibold">Notification Settings</h2>

        <form onSubmit={handleSubmitNotifications(data => updateNotificationsMutation.mutate(data))} className="space-y-4">
          <div className="flex items-center">
            <input type="checkbox" {...registerNotifications('enableEmail')} className="mr-2" />
            <label className="text-sm">Enable Email Notifications</label>
          </div>

          <div className="flex items-center">
            <input type="checkbox" {...registerNotifications('notifyOnStatusChange')} className="mr-2" />
            <label className="text-sm">Notify on Status Changes</label>
          </div>

          <div className="flex items-center">
            <input type="checkbox" {...registerNotifications('dailySummary')} className="mr-2" />
            <label className="text-sm">Send Daily Summary Reports</label>
          </div>

          <div className="flex items-center">
            <input type="checkbox" {...registerNotifications('newCustomerAlerts')} className="mr-2" />
            <label className="text-sm">New Customer Alerts</label>
          </div>

          <div className="pt-4">
            <button type="submit" className="btn btn-primary" disabled={updateNotificationsMutation.isLoading}>
              {updateNotificationsMutation.isLoading ? 'Saving...' : 'Save Notification Settings'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Security Settings Tab
const SecuritySettings = () => {
  const queryClient = useQueryClient();

  const { data: settings, isLoading } = useQuery('security-settings', async () => {
    const res = await api.get('/settings/security');
    return res.data.data;
  });

  const updateMutation = useMutation(
    (data) => api.put('/settings', { category: 'security', settings: data }),
    {
      onSuccess: () => {
        toast.success('Security settings updated');
        queryClient.invalidateQueries('security-settings');
      }
    }
  );

  const { register, handleSubmit } = useForm({
    values: settings || {}
  });

  const { register: registerPassword, handleSubmit: handleSubmitPassword, formState: { errors }, watch, reset } = useForm();

  const changePasswordMutation = useMutation(
    (data) => api.post('/auth/change-password', data),
    {
      onSuccess: () => {
        toast.success('Password changed successfully');
        reset();
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Failed to change password');
      }
    }
  );

  const onSubmit = (data) => {
    updateMutation.mutate({
      ...data,
      sessionTimeout: parseInt(data.sessionTimeout),
      passwordMinLength: parseInt(data.passwordMinLength)
    });
  };

  const onPasswordSubmit = (data) => {
    changePasswordMutation.mutate({
      currentPassword: data.currentPassword,
      newPassword: data.newPassword
    });
  };

  if (isLoading) return <div>Loading...</div>;

  return (
    <div className="space-y-6">
      {/* Change Password Section */}
      <div className="card space-y-6">
        <h2 className="text-xl font-semibold">Change Password</h2>

        <form onSubmit={handleSubmitPassword(onPasswordSubmit)} className="space-y-4 max-w-md">
          <div>
            <label className="label">Current Password</label>
            <input
              type="password"
              {...registerPassword('currentPassword', { required: 'Current password is required' })}
              className="input"
            />
            {errors.currentPassword && <span className="text-red-500 text-sm">{errors.currentPassword.message}</span>}
          </div>

          <div>
            <label className="label">New Password</label>
            <input
              type="password"
              {...registerPassword('newPassword', {
                required: 'New password is required',
                minLength: { value: 12, message: 'Password must be at least 12 characters' }
              })}
              className="input"
            />
            {errors.newPassword && <span className="text-red-500 text-sm">{errors.newPassword.message}</span>}
          </div>

          <div>
            <label className="label">Confirm New Password</label>
            <input
              type="password"
              {...registerPassword('confirmPassword', {
                required: 'Please confirm your password',
                validate: (value) => value === watch('newPassword') || 'Passwords do not match'
              })}
              className="input"
            />
            {errors.confirmPassword && <span className="text-red-500 text-sm">{errors.confirmPassword.message}</span>}
          </div>

          <div className="pt-4">
            <button type="submit" className="btn btn-primary" disabled={changePasswordMutation.isLoading}>
              {changePasswordMutation.isLoading ? 'Changing...' : 'Change Password'}
            </button>
          </div>
        </form>
      </div>

      {/* Security Configuration Section */}
      <div className="card space-y-6">
        <h2 className="text-xl font-semibold">Security Configuration</h2>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div>
            <label className="label">Session Timeout (minutes)</label>
            <select {...register('sessionTimeout')} className="input">
              <option value="15">15 minutes</option>
              <option value="30">30 minutes</option>
              <option value="60">1 hour</option>
              <option value="120">2 hours</option>
              <option value="240">4 hours</option>
            </select>
          </div>

          <div>
            <label className="label">Minimum Password Length</label>
            <input type="number" min="6" max="20" {...register('passwordMinLength')} className="input" />
          </div>

          <div className="flex items-center">
            <input type="checkbox" {...register('requireStrongPassword')} className="mr-2" />
            <label className="text-sm">Require Strong Password (uppercase, lowercase, number, special character)</label>
          </div>

          <div className="flex items-center">
            <input type="checkbox" {...register('allowMultipleSessions')} className="mr-2" />
            <label className="text-sm">Allow Multiple Sessions per User</label>
          </div>

          <div className="flex items-center">
            <input type="checkbox" {...register('autoLogoutOnInactivity')} className="mr-2" />
            <label className="text-sm">Auto Logout on Inactivity</label>
          </div>

          <div className="pt-4">
            <button type="submit" className="btn btn-primary" disabled={updateMutation.isLoading}>
              {updateMutation.isLoading ? 'Saving...' : 'Save Security Settings'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Data Backup Tab
const DataBackup = () => {
  const queryClient = useQueryClient();
  const [exporting, setExporting] = useState(false);
  const [selectedFormat, setSelectedFormat] = useState('xlsx');
  const [restoreFile, setRestoreFile] = useState(null);
  const [restoring, setRestoring] = useState(false);
  const [confirmRestore, setConfirmRestore] = useState(false);
  const restoreInputRef = useRef(null);

  const { data: restorePoints, refetch: refetchRestorePoints } = useQuery('restorePoints', async () => {
    const res = await api.get('/customers/restore-points');
    return res.data.data;
  });

  const handleRestoreFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      const fileExt = selectedFile.name.split('.').pop().toLowerCase();
      if (['csv', 'xlsx', 'xls', 'json'].includes(fileExt)) {
        setRestoreFile(selectedFile);
        setConfirmRestore(false);
      } else {
        toast.error('Please select a CSV, Excel, or JSON backup file');
        e.target.value = '';
      }
    }
  };

  const handleRestore = async () => {
    if (!restoreFile) return;

    setRestoring(true);
    const formData = new FormData();
    formData.append('file', restoreFile);

    try {
      const response = await api.post('/customers/restore', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      toast.success(`Database restored: ${response.data.data.imported} customers`);
      queryClient.invalidateQueries();
      refetchRestorePoints();
      setRestoreFile(null);
      setConfirmRestore(false);
      if (restoreInputRef.current) restoreInputRef.current.value = '';
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to restore from backup');
    } finally {
      setRestoring(false);
    }
  };

  const { data: stats } = useQuery('statistics', async () => {
    const res = await api.get('/customers/statistics');
    return res.data.data;
  });

  const handleExport = async (format) => {
    setExporting(true);
    setSelectedFormat(format);

    try {
      const response = await api.get(`/customers/export?format=${format}`, {
        responseType: 'blob'
      });

      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;

      const date = new Date().toISOString().split('T')[0];
      const extensions = { json: 'json', csv: 'csv', xlsx: 'xlsx' };
      link.setAttribute('download', `customers_backup_${date}.${extensions[format]}`);

      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast.success(`Backup exported successfully as ${format.toUpperCase()}`);
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Failed to export backup');
    } finally {
      setExporting(false);
    }
  };

  const formatOptions = [
    {
      id: 'xlsx',
      name: 'Excel (.xlsx)',
      description: 'Best for spreadsheet applications like Microsoft Excel or Google Sheets',
      icon: FileSpreadsheet,
      color: 'bg-green-500'
    },
    {
      id: 'csv',
      name: 'CSV (.csv)',
      description: 'Universal format compatible with most applications',
      icon: FileText,
      color: 'bg-blue-500'
    },
    {
      id: 'json',
      name: 'JSON (.json)',
      description: 'Best for data backup and restoration, preserves all data types',
      icon: FileJson,
      color: 'bg-purple-500'
    }
  ];

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-semibold">Data Backup</h2>
      </div>

      {/* Database Stats */}
      <div className="card">
        <h3 className="text-lg font-medium mb-4">Database Overview</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-blue-50 rounded-lg p-4">
            <p className="text-sm text-blue-600">Total Customers</p>
            <p className="text-2xl font-bold text-blue-700">{stats?.total || 0}</p>
          </div>
          <div className="bg-green-50 rounded-lg p-4">
            <p className="text-sm text-green-600">Active</p>
            <p className="text-2xl font-bold text-green-700">
              {stats?.byStatus?.filter(s => s.status === 'Active' || s.status === 'Done').reduce((sum, s) => sum + parseInt(s.count), 0) || 0}
            </p>
          </div>
          <div className="bg-yellow-50 rounded-lg p-4">
            <p className="text-sm text-yellow-600">In Process</p>
            <p className="text-2xl font-bold text-yellow-700">
              {stats?.byStatus?.find(s => s.status === 'In Process')?.count || 0}
            </p>
          </div>
          <div className="bg-purple-50 rounded-lg p-4">
            <p className="text-sm text-purple-600">Areas</p>
            <p className="text-2xl font-bold text-purple-700">{stats?.byArea?.length || 0}</p>
          </div>
        </div>
      </div>

      {/* Export Options */}
      <div className="card">
        <h3 className="text-lg font-medium mb-4">Export Format</h3>
        <p className="text-sm text-gray-600 mb-6">
          Choose a format to export all customer data. The backup will include all fields and records.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {formatOptions.map((format) => {
            const Icon = format.icon;
            return (
              <div
                key={format.id}
                className={`border-2 rounded-lg p-6 cursor-pointer transition-all hover:shadow-md ${
                  selectedFormat === format.id ? 'border-primary-500 bg-primary-50' : 'border-gray-200 hover:border-gray-300'
                }`}
                onClick={() => setSelectedFormat(format.id)}
              >
                <div className="flex items-center gap-3 mb-3">
                  <div className={`p-2 rounded-lg ${format.color}`}>
                    <Icon className="w-6 h-6 text-white" />
                  </div>
                  <h4 className="font-semibold">{format.name}</h4>
                </div>
                <p className="text-sm text-gray-600">{format.description}</p>
              </div>
            );
          })}
        </div>

        <div className="mt-6 flex gap-4">
          <button
            onClick={() => handleExport(selectedFormat)}
            disabled={exporting}
            className="btn btn-primary flex items-center gap-2"
          >
            <Download className="w-4 h-4" />
            {exporting ? 'Exporting...' : `Export as ${selectedFormat.toUpperCase()}`}
          </button>
        </div>
      </div>

      {/* Restore from Backup */}
      <div className="card">
        <h3 className="text-lg font-medium mb-4">Restore from Backup</h3>
        <p className="text-sm text-gray-600 mb-4">
          Replace all customer data with a backup file. Upload a file you exported from this or another system.
        </p>

        <div className="flex items-center gap-4">
          <label htmlFor="restore-upload" className="btn btn-secondary cursor-pointer flex items-center gap-2">
            <Upload className="w-4 h-4" />
            Select Backup File
            <input
              id="restore-upload"
              ref={restoreInputRef}
              type="file"
              accept=".csv,.xlsx,.xls,.json"
              onChange={handleRestoreFileChange}
              className="hidden"
            />
          </label>
          {restoreFile && (
            <span className="text-sm text-gray-600">
              {restoreFile.name} ({(restoreFile.size / 1024).toFixed(1)} KB)
            </span>
          )}
        </div>

        {restoreFile && !confirmRestore && (
          <div className="mt-4">
            <button
              onClick={() => setConfirmRestore(true)}
              className="btn bg-red-600 text-white hover:bg-red-700 flex items-center gap-2"
            >
              <RotateCcw className="w-4 h-4" />
              Restore Database
            </button>
          </div>
        )}

        {confirmRestore && (
          <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-sm text-red-700 font-medium mb-3">
              This will delete all current customers and replace them with the backup. Are you sure?
            </p>
            <div className="flex gap-3">
              <button
                onClick={handleRestore}
                disabled={restoring}
                className="btn bg-red-600 text-white hover:bg-red-700"
              >
                {restoring ? 'Restoring...' : 'Yes, Replace All Data'}
              </button>
              <button
                onClick={() => {
                  setConfirmRestore(false);
                  setRestoreFile(null);
                  if (restoreInputRef.current) restoreInputRef.current.value = '';
                }}
                className="btn btn-secondary"
              >
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Restore Points */}
      {restorePoints && restorePoints.length > 0 && (
        <div className="card">
          <h3 className="text-lg font-medium mb-4">Restore Points</h3>
          <p className="text-sm text-gray-600 mb-4">
            A restore point is automatically saved before each database restore. Download one and use it to undo a restore.
          </p>
          <div className="space-y-2">
            {restorePoints.map((rp) => (
              <div key={rp.filename} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border">
                <div className="text-sm">
                  <span className="font-medium">{rp.customers}</span>
                  <span className="text-gray-500 ml-3">{new Date(rp.createdAt).toLocaleString()}</span>
                  <span className="text-gray-400 ml-3">({(rp.size / 1024).toFixed(0)} KB)</span>
                </div>
                <button
                  onClick={async () => {
                    try {
                      const response = await api.get(`/customers/restore-points/${rp.filename}`, { responseType: 'blob' });
                      const url = window.URL.createObjectURL(new Blob([response.data]));
                      const a = document.createElement('a');
                      a.href = url;
                      a.download = rp.filename;
                      a.click();
                      window.URL.revokeObjectURL(url);
                    } catch { toast.error('Failed to download restore point'); }
                  }}
                  className="btn btn-secondary btn-sm flex items-center gap-1 text-xs"
                >
                  <Download className="w-3 h-3" />
                  Download
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Backup Information */}
      <div className="card bg-gray-50">
        <h3 className="text-lg font-medium mb-4">Backup Information</h3>
        <ul className="list-disc list-inside space-y-2 text-sm text-gray-600">
          <li>Backups include all customer records with all fields</li>
          <li>Export includes: name, phone, email, address, GPS coordinates, status, CYTA details, and dates</li>
          <li>JSON format is recommended for full data restoration — it preserves all data types</li>
          <li>CSV and Excel formats are best for viewing and editing in spreadsheet applications</li>
          <li>To transfer data to another PC: export here, then restore on the other system</li>
          <li>A restore point is automatically created before each database restore</li>
          <li>Automated backups run every 2 days (database + files)</li>
        </ul>
      </div>
    </div>
  );
};

// Data Import Tab
const DataImport = () => {
  const queryClient = useQueryClient();
  const [file, setFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [preview, setPreview] = useState(null);

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      const fileExt = selectedFile.name.split('.').pop().toLowerCase();
      if (['csv', 'xlsx', 'xls', 'ods'].includes(fileExt)) {
        setFile(selectedFile);
        // Preview first few rows (only for CSV)
        if (fileExt === 'csv') {
          previewFile(selectedFile);
        } else {
          setPreview([`File: ${selectedFile.name}`, `Type: ${fileExt.toUpperCase()}`, 'Preview not available for this format']);
        }
      } else {
        toast.error('Please select a CSV, Excel, or ODS file');
        e.target.value = '';
      }
    }
  };

  const previewFile = (file) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target.result;
      const lines = text.split('\n').slice(0, 6); // First 6 lines (header + 5 rows)
      setPreview(lines);
    };
    reader.readAsText(file);
  };

  const handleImport = async () => {
    if (!file) {
      toast.error('Please select a file first');
      return;
    }

    setImporting(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await api.post('/customers/import', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      const { imported, skipped, errors } = response.data.data;

      if (imported > 0) {
        toast.success(`Successfully imported ${imported} customers`);
      }

      if (skipped > 0) {
        toast.error(`${skipped} rows failed to import`, { duration: 5000 });
        if (errors && errors.length > 0) {
          console.log('Import errors:', errors);
        }
      }

      if (imported === 0 && skipped === 0) {
        toast.error('No customers found in file');
      }

      setFile(null);
      setPreview(null);
      queryClient.invalidateQueries('customers');
      queryClient.invalidateQueries('statistics');
    } catch (error) {
      console.error('Import error:', error);
      toast.error(error.response?.data?.message || 'Failed to import customers');
    } finally {
      setImporting(false);
    }
  };

  const downloadTemplate = () => {
    const template = `customerName,phone,email,fullAddress,area,status,remarks,cytaFiberCode,cytaReference,cytaCircuitNumber,gponSerial,cytaPlan,latitude,longitude
John Doe,99123456,john@example.com,"123 Main St, Paphos",Paphos,Not process,Test customer,FC001,REF001,CIRC001,GPON001,100Mbps,34.7772,32.4249
Jane Smith,99654321,jane@example.com,"456 Beach Rd, Geroskipou",Geroskipou,In process,VIP customer,FC002,REF002,CIRC002,GPON002,200Mbps,34.7565,32.4571`;

    const blob = new Blob([template], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'customer_import_template.csv';
    a.click();
    window.URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-semibold">Data Import</h2>
        <button onClick={downloadTemplate} className="btn btn-secondary flex items-center gap-2">
          <Upload className="w-4 h-4" />
          Download Template
        </button>
      </div>

      <div className="card space-y-6">
        <div>
          <h3 className="text-lg font-medium mb-4">Import Customers from CSV/Excel</h3>
          <p className="text-sm text-gray-600 mb-4">
            Upload a CSV or Excel file to import multiple customers at once. Download the template to see the required format.
          </p>

          <div className="border-2 border-dashed border-gray-300 rounded-lg p-8">
            <div className="flex flex-col items-center justify-center space-y-4">
              <Upload className="w-12 h-12 text-gray-400" />
              <div className="text-center">
                <label htmlFor="file-upload" className="btn btn-primary cursor-pointer">
                  Select File
                  <input
                    id="file-upload"
                    type="file"
                    accept=".csv,.xlsx,.xls,.ods"
                    onChange={handleFileChange}
                    className="hidden"
                  />
                </label>
                <p className="text-sm text-gray-500 mt-2">or drag and drop</p>
                <p className="text-xs text-gray-400 mt-1">CSV, XLSX, XLS, ODS (MAX 10MB)</p>
              </div>
              {file && (
                <div className="text-sm text-green-600 font-medium">
                  Selected: {file.name}
                </div>
              )}
            </div>
          </div>

          {preview && (
            <div className="mt-4">
              <h4 className="font-medium mb-2">Preview (First 5 rows):</h4>
              <div className="bg-gray-50 p-4 rounded border overflow-x-auto">
                <pre className="text-xs">{preview.join('\n')}</pre>
              </div>
            </div>
          )}

          {file && (
            <div className="mt-4 flex gap-4">
              <button
                onClick={handleImport}
                disabled={importing}
                className="btn btn-primary"
              >
                {importing ? 'Importing...' : 'Import Customers'}
              </button>
              <button
                onClick={() => {
                  setFile(null);
                  setPreview(null);
                }}
                className="btn btn-secondary"
              >
                Cancel
              </button>
            </div>
          )}
        </div>

        <div className="border-t pt-6">
          <h4 className="font-medium mb-3">Import Instructions:</h4>
          <ul className="list-disc list-inside space-y-2 text-sm text-gray-600">
            <li>Download the template to see the correct column format</li>
            <li>Required fields: customerName, phone, fullAddress</li>
            <li>Optional fields: email, area, status, remarks, and CYTA details</li>
            <li>For coordinates, provide latitude and longitude (e.g., 34.7772, 32.4249)</li>
            <li>Status values should match your configured status options</li>
            <li>Area values should match your configured area options</li>
            <li>Duplicate phone numbers will be skipped</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default Settings;
